/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type {
  GetRuoliAssegnabiliBackofficeParams,
  GetUtentiAssegnabiliBackofficeParams,
  Utente,
  WsRispostaPaginataListRuoloExt,
  WsRispostaPaginataListUtente,
} from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce un utente a partire da id con ruoli e uffici filtrati
 * @summary Restituisce utente a partire da id
 */
export const getUtentiAssegnabiliBackofficeId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Utente>(
    {
      url: `/framework-cfg-utenti/utenti-assegnabili-backoffice/${id}`,
      method: 'get',
      signal,
    },
    options,
  );
};

export const getGetUtentiAssegnabiliBackofficeIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/utenti-assegnabili-backoffice/${id}`] as const;

export const getGetUtentiAssegnabiliBackofficeIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUtentiAssegnabiliBackofficeIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>
  > = ({ signal }) =>
    getUtentiAssegnabiliBackofficeId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetUtentiAssegnabiliBackofficeIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>
>;
export type GetUtentiAssegnabiliBackofficeIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce utente a partire da id
 */
export const useGetUtentiAssegnabiliBackofficeId = <
  TData = Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiAssegnabiliBackofficeId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiAssegnabiliBackofficeIdQueryOptions(
    id,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna un utente
 * @summary Aggiorna un utente
 */
export const putUtentiAssegnabiliBackofficeId = (
  id: string,
  utente: Utente,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/utenti-assegnabili-backoffice/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: utente,
    },
    options,
  );
};

export const getPutUtentiAssegnabiliBackofficeIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiAssegnabiliBackofficeId>>,
    TError,
    { id: string; data: Utente },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putUtentiAssegnabiliBackofficeId>>,
  TError,
  { id: string; data: Utente },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putUtentiAssegnabiliBackofficeId>>,
    { id: string; data: Utente }
  > = props => {
    const { id, data } = props ?? {};

    return putUtentiAssegnabiliBackofficeId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutUtentiAssegnabiliBackofficeIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putUtentiAssegnabiliBackofficeId>>
>;
export type PutUtentiAssegnabiliBackofficeIdMutationBody = Utente;
export type PutUtentiAssegnabiliBackofficeIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna un utente
 */
export const usePutUtentiAssegnabiliBackofficeId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putUtentiAssegnabiliBackofficeId>>,
    TError,
    { id: string; data: Utente },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions =
    getPutUtentiAssegnabiliBackofficeIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Ritorna la lista degli utenti figli associati all'id padre
 * @summary lista utenti figli associati all'id padre
 */
export const getUtentiAssegnabiliBackoffice = (
  params?: GetUtentiAssegnabiliBackofficeParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListUtente>(
    {
      url: `/framework-cfg-utenti/utenti-assegnabili-backoffice`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetUtentiAssegnabiliBackofficeQueryKey = (
  params?: GetUtentiAssegnabiliBackofficeParams,
) =>
  [
    `/framework-cfg-utenti/utenti-assegnabili-backoffice`,
    ...(params ? [params] : []),
  ] as const;

export const getGetUtentiAssegnabiliBackofficeQueryOptions = <
  TData = Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUtentiAssegnabiliBackofficeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUtentiAssegnabiliBackofficeQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>
  > = ({ signal }) =>
    getUtentiAssegnabiliBackoffice(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetUtentiAssegnabiliBackofficeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>
>;
export type GetUtentiAssegnabiliBackofficeQueryError = ErrorType<unknown>;

/**
 * @summary lista utenti figli associati all'id padre
 */
export const useGetUtentiAssegnabiliBackoffice = <
  TData = Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>,
  TError = ErrorType<unknown>,
>(
  params?: GetUtentiAssegnabiliBackofficeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getUtentiAssegnabiliBackoffice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetUtentiAssegnabiliBackofficeQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 * @summary Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 */
export const getRuoliAssegnabiliBackoffice = (
  params?: GetRuoliAssegnabiliBackofficeParams,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<WsRispostaPaginataListRuoloExt>(
    {
      url: `/framework-cfg-utenti/ruoli-assegnabili-backoffice`,
      method: 'get',
      params,
      signal,
    },
    options,
  );
};

export const getGetRuoliAssegnabiliBackofficeQueryKey = (
  params?: GetRuoliAssegnabiliBackofficeParams,
) =>
  [
    `/framework-cfg-utenti/ruoli-assegnabili-backoffice`,
    ...(params ? [params] : []),
  ] as const;

export const getGetRuoliAssegnabiliBackofficeQueryOptions = <
  TData = Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliAssegnabiliBackofficeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetRuoliAssegnabiliBackofficeQueryKey(params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>
  > = ({ signal }) =>
    getRuoliAssegnabiliBackoffice(params, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetRuoliAssegnabiliBackofficeQueryResult = NonNullable<
  Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>
>;
export type GetRuoliAssegnabiliBackofficeQueryError = ErrorType<unknown>;

/**
 * @summary Cerca uno o più ruoli utente. Ordinabile per descrizione, denominazione, id
 */
export const useGetRuoliAssegnabiliBackoffice = <
  TData = Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>,
  TError = ErrorType<unknown>,
>(
  params?: GetRuoliAssegnabiliBackofficeParams,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getRuoliAssegnabiliBackoffice>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetRuoliAssegnabiliBackofficeQueryOptions(
    params,
    options,
  );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};
